import { Timer } from '@visiba/std';
import { AnimatePresence, motion, PanInfo, useAnimation, useDragControls } from 'framer-motion';
import { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { restrictedZIndexes } from '../../utils';
import { useText } from '../../utils/hooks/use_text';
import { Icon, IconDictionary } from '../icon';
import { IconButton } from '../icon_button';
import { Text } from '../text';
import { AppNotificationsCtx } from './app_notifications_provider';
import { ToastNotification, Variants } from './toast_notification';
import { theme } from './toast_notification.theme';

interface Props {}

const elements = {
	container: styled.div`
		position: fixed;
		right: 0;
		bottom: ${coreTokens.spacing.x8};
		z-index: ${restrictedZIndexes.DANGER};
		display: flex;
		flex-direction: column;
		pointer-events: none;
		width: 100%;

		${coreTokens.breakpointQuery.medium} {
			right: 20px;
			width: 448px;
			bottom: ${coreTokens.spacing.x2};
		}
	`,
	list: styled.ol`
		all: unset;
	`,
	listItemParent: styled(motion.li)`
		display: flex;
		flex-direction: column;
		margin: 10px;
	`,
	listItem: styled(motion.div)`
		&[data-variant='success'] {
			--_background: ${theme.getValue('successBackground')};
			--_border: ${theme.getValue('successBorderColor')};
			--_color: ${theme.getValue('successTextColor')};
			--_iconColor: ${theme.getValue('successIconColor')};
		}
		&[data-variant='warning'] {
			--_background: ${theme.getValue('warningBackground')};
			--_border: ${theme.getValue('warningBorderColor')};
			--_color: ${theme.getValue('warningTextColor')};
			--_iconColor: ${theme.getValue('warningIconColor')};
		}
		&[data-variant='danger'] {
			--_background: ${theme.getValue('dangerBackground')};
			--_border: ${theme.getValue('dangerBorderColor')};
			--_color: ${theme.getValue('dangerTextColor')};
			--_iconColor: ${theme.getValue('dangerIconColor')};
		}
		&[data-variant='info'] {
			--_background: ${theme.getValue('infoBackground')};
			--_border: ${theme.getValue('infoBorderColor')};
			--_color: ${theme.getValue('infoTextColor')};
			--_iconColor: ${theme.getValue('infoIconColor')};
		}

		pointer-events: all;
		display: flex;
		background-color: var(--_background);
		border: 1px solid var(--_border);
		color: var(--_content);
		padding: ${theme.getValue('basePadding')};
		gap: ${theme.getValue('baseGap')};
		border-radius: ${theme.getValue('baseBorderRadius')};
		width: inherit;
		box-shadow: ${theme.getValue('baseElevation')};
	`,
	text: styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
	`,
	iconWrapper: styled.div`
		color: var(--_iconColor);
	`,
};

export function ToastNotificaion(_: Props): JSX.Element {
	const appNotificationCtx = useContext(AppNotificationsCtx);
	const [notifications, setNotifications] = useState<ToastNotification[]>([]);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const toastRef = useRef<HTMLLIElement>(null);
	const listRef = useRef<HTMLOListElement>(null);
	const closeTimerRef = useRef<Timer>(new Timer());
	const removeNotification = useCallback(
		(target: ToastNotification) => {
			appNotificationCtx.remove(target);
			setNotifications([...appNotificationCtx.get('toast')]);

			const isFocusInToast = toastRef.current?.contains(document.activeElement);
			if (isFocusInToast && appNotificationCtx.count('toast') > 0) listRef.current?.focus();
		},
		[appNotificationCtx],
	);

	useEffect(
		function toHandleServiceEvents() {
			function handleChange(_: ToastNotification): void {
				setNotifications([...appNotificationCtx.get('toast')]);
			}

			appNotificationCtx.eventEmitter.on('onAddToast', handleChange);
			appNotificationCtx.eventEmitter.on('onRemoveToast', handleChange);

			return () => {
				appNotificationCtx.eventEmitter.off('onAddToast', handleChange);
				appNotificationCtx.eventEmitter.off('onRemoveToast', handleChange);
			};
		},
		[appNotificationCtx],
	);

	useEffect(function toHandleKeyboardShortcut() {
		function handleKeyDown(event: KeyboardEvent): void {
			const isHotkeyPressed = ['f8'].every((key) => event.code?.toLowerCase() === key.toLowerCase());
			if (isHotkeyPressed) listRef.current?.focus();
		}
		document.addEventListener('keydown', handleKeyDown);

		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	useEffect(
		function toHandleCloseTimer() {
			closeTimerRef.current.flush();

			const addedNotification = notifications[notifications.length - 1];

			return closeTimerRef.current.wait(
				() => {
					const localPushes = appNotificationCtx.get('toast');
					const toBeRemoved = localPushes[0];

					if (toBeRemoved != null) {
						removeNotification(toBeRemoved);
					}
				},
				addedNotification?.duration ?? 5 * 1000,
			);
		},
		[notifications, appNotificationCtx, removeNotification],
	);

	function handleClose(target: ToastNotification): void {
		removeNotification(target);
	}

	return (
		<elements.container
			ref={wrapperRef}
			role='region'
			aria-label='Notifications'
			tabIndex={-1}
			aria-hidden={notifications.length === 0}
		>
			{/**
			 * tabindex on the the list so that it can be focused when items are removed. we focus
			 * the list instead of the viewport so it announces number of items remaining.
			 */}
			<elements.list tabIndex={-1} ref={listRef}>
				<AnimatePresence initial={false}>
					{notifications.map((notification, index) => {
						return (
							<Toast
								key={notification.guid}
								ref={index === 0 ? toastRef : undefined}
								notification={notification}
								onClose={handleClose}
							/>
						);
					})}
				</AnimatePresence>
			</elements.list>
		</elements.container>
	);
}

interface ToastProps {
	notification: ToastNotification;
	onClose: (target: ToastNotification) => void;
}

const Toast = forwardRef<HTMLLIElement, ToastProps>(function Toast(props, forwardedRef): JSX.Element {
	function handleOnKeyDown(target: ToastNotification): (event: React.KeyboardEvent<HTMLLIElement>) => void {
		return (event) => {
			if (event.key !== 'Escape') return;

			if (!event.nativeEvent.defaultPrevented) {
				props.onClose(target);
			}
		};
	}

	return (
		<elements.listItemParent
			role='status'
			aria-live='off'
			tabIndex={0}
			ref={forwardedRef}
			layout
			initial={{ opacity: 0, y: 50, scale: 1 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.2 } }}
			transition={{
				type: 'spring',
				stiffness: 240,
				damping: 20,
			}}
			onKeyDown={handleOnKeyDown(props.notification)}
		>
			<Inner {...props} />
		</elements.listItemParent>
	);
});

const CLOSE_VELOCITY_THRESHOLD = 100;
const CLOSE_DRAGGED_PX_THRESHOLD = 10;

function Inner(props: ToastProps): JSX.Element {
	// Storing the panInfo from the previously drag event due to https://github.com/framer/motion/issues/1087
	const panInfoCache = useRef<PanInfo | null>(null);
	const dragControls = useDragControls();
	const animation = useAnimation();
	const texts = useText();

	function exitThreshold(y: number, velocity: number | null): boolean {
		checkVelocity: {
			if (velocity == null) break checkVelocity;
			if (velocity < CLOSE_VELOCITY_THRESHOLD) {
				return false;
			}
		}

		return y > CLOSE_DRAGGED_PX_THRESHOLD;
	}

	function handleDragEnd(_: MouseEvent, __: PanInfo): void {
		if (panInfoCache.current == null) return;

		const result = exitThreshold(panInfoCache.current.offset.y, panInfoCache.current.velocity.y);
		if (result) {
			props.onClose(props.notification);
			animation.start('down');
		}

		panInfoCache.current = null;
	}

	function handleDrag(_: MouseEvent, panInfo: PanInfo): void {
		panInfoCache.current = panInfo;
	}

	function handleOnClick(target: ToastNotification): (event: React.MouseEvent<HTMLButtonElement>) => void {
		return (_) => {
			props.onClose(target);
		};
	}

	return (
		<elements.listItem
			dragControls={dragControls}
			data-variant={props.notification.variant}
			drag='y'
			dragConstraints={{
				top: 0,
				bottom: 100,
			}}
			dragSnapToOrigin={true}
			onDragEnd={handleDragEnd}
			onDrag={handleDrag}
			initial='initial'
			animate={animation}
			transition={{
				type: 'spring',
				damping: 25,
				stiffness: 100,
			}}
			variants={{
				up: {
					y: '-300%',
				},
				initial: {
					y: 0,
				},
				down: {
					y: '300%',
				},
			}}
		>
			<elements.iconWrapper>
				<Icon type={extractIcon(props.notification.variant)} />
			</elements.iconWrapper>

			<elements.text>
				<Text variant='labelLargeSemiBold'>{props.notification.label}</Text>
				{props.notification.message != null ? <Text variant='labelRegular'>{props.notification.message}</Text> : null}
			</elements.text>

			<IconButton
				variant={props.notification.variant}
				size='small'
				text={texts('Common.Close')}
				icon='x'
				onClick={handleOnClick(props.notification)}
			/>
		</elements.listItem>
	);
}

function extractIcon(variant: Variants): keyof IconDictionary {
	switch (variant) {
		case 'success':
			return 'check';
		case 'danger':
			return 'warning';
		case 'info':
			return 'warningCircle';
		case 'warning':
			return 'warning';
	}
}
